import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Fire-and-forget conversion ping. Backend route is anonymous-friendly
 * so we never block the user's nav to /billing on the network call.
 */
function _trackUpgradeCta(feature) {
  try {
    api.post("/analytics/upgrade-cta-clicked", {
      feature: feature || null,
      from_url: typeof window !== "undefined" ? window.location.pathname : null,
    }).catch(() => {});  // swallow — tracking failures must NEVER surface
  } catch { /* no-op */ }
}

/**
 * Smart error toast handler.
 *
 * If the response is a structured `feature_locked` 402, renders a richer
 * toast with an "Upgrade now" action button that deep-links to /billing.
 * Otherwise falls back to a plain `toast.error(title, {description})`.
 *
 * Usage:
 *   try { ... } catch (err) {
 *     handleApiError(err, "Couldn't create link", navigate);
 *   }
 *
 * `navigate` is the react-router `useNavigate()` callback. When omitted,
 * the upgrade action uses a full page reload to `/billing` as fallback.
 */
export function handleApiError(err, fallbackTitle = "Something went wrong", navigate = null) {
  const detail = err?.response?.data?.detail;
  const code = typeof detail === "object" && detail !== null ? detail.code : null;

  if (code === "feature_locked") {
    const featureRaw = (detail.feature || "this feature").replace(/_/g, " ");
    // Title-case so "pdf reports" -> "PDF Reports" (most feature names are
    // either single-word or all-caps acronyms like PDF / API / SLA).
    const ACRONYMS = new Set(["pdf", "api", "sla", "ai", "url", "id"]);
    const feature = featureRaw
      .split(" ")
      .map((w) => {
        if (!w) return w;
        if (ACRONYMS.has(w.toLowerCase())) return w.toUpperCase();
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(" ");
    toast.error(
      `Unlock ${feature}`,
      {
        description: detail.message || "Upgrade your plan to keep going.",
        duration: 12000,
        action: {
          label: "Upgrade now",
          onClick: () => {
            _trackUpgradeCta(detail.feature);
            if (navigate) navigate("/billing");
            else window.location.href = "/billing";
          },
        },
        // Larger so the action button has breathing room.
        className: "min-w-[420px]",
      },
    );
    return;
  }

  // Rate-limit hint — slightly friendlier copy. Falls through to normal
  // error toast for everything else.
  if (code && code.startsWith("rate_limit_")) {
    toast.error("Slow down a sec", {
      description: detail.message || "Too many requests — try again in a minute.",
      duration: 8000,
    });
    return;
  }

  toast.error(fallbackTitle, { description: formatApiError(err) });
}
